import Button from "components/button/button"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { HiArrowNarrowLeft, HiArrowNarrowRight, HiOutlineChevronLeft, HiOutlineChevronRight } from "react-icons/hi"

type TablePaginationProps = {
   currentPage: number
   disabled?: boolean
   total: number
   onChange?: (value: number) => void
}

const TablePagination: React.FC<TablePaginationProps> = ({ disabled, currentPage, total, onChange }: TablePaginationProps) => {
   const [t] = useTranslation()

   const [value, setValue] = useState<number>(currentPage + 1)

   useEffect(() => {
      setValue(currentPage + 1)
   }, [currentPage])

   const handlePrevPage = (e?: React.MouseEvent) => {
      e?.stopPropagation()
      if (value > 1) {
         const newValue = value - 1
         setValue(newValue)
         onChange?.(newValue)
      }
   }

   const handleNextPage = (e?: React.MouseEvent) => {
      e?.stopPropagation()
      if (value < total) {
         const newValue = value + 1
         setValue?.(newValue)
         onChange?.(newValue)
      }
   }

   const handlePageNumberPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
      const paste = e.clipboardData.getData("text")
      const newValue = paste.replace(/\D/g, "")
      const number = parseInt(newValue, 10)

      if (!/^\d{1,3}$/.test(newValue) || number > total) {
         e.preventDefault()
      }
   }

   const handlePageNumberChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value
      if (!/^\d*$/.test(inputValue)) return

      if (inputValue !== "") {
         if (inputValue.length < 4 && Number(inputValue) <= total) {
            setValue(parseInt(inputValue, 10))
         } else {
            // Do nothing
         }
      } else {
         setValue(0)
      }
   }

   const handlePageNumberKeydown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      const allowedKeys = [
         "Backspace",
         "ArrowLeft",
         "ArrowRight",
         "Tab",
         "Delete"
      ]

      if (
         !/^\d$/.test(e.key) &&
         !allowedKeys.includes(e.key)
      ) {
         e.preventDefault()
      }

      if (e.key === "Enter") {
         onChange?.(value)
      }
   }

   return (
      <div className="grid md:grid-cols-3 grid-cols-1 items-center gap-x-2.5">
         {/* Status */}
         <div className="flex invisible">
            {`Showing 1-10 of 1000`}
         </div>
         {/* Navigation */}
         <div className="flex justify-center">
            <div className="flex gap-x-4">
               <Button
                  type="button"
                  className="rounded-md text-black !border-[#e8e8e8] bg-white h-8.5 group hover:shadow-[0px_0px_4px_2px_#85ECF766]"
                  disabled={disabled || currentPage <= 0}
                  outline
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => handlePrevPage(e)}
               >
                  <HiArrowNarrowLeft color="black" size={18} className="mr-2.5 group-hover:fill-white" />
                  <span className="text-xs font-semibold group-hover:text-white">{t('common.prevPage')}</span>
               </Button>
               <Button
                  type="button"
                  className="rounded-md h-8.5 hover:shadow-[0px_0px_4px_2px_#85ECF766]"
                  disabled={disabled || currentPage >= total - 1}
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleNextPage(e)}
               >
                  <span className="text-xs font-semibold">{t('common.nextPage')}</span>
                  <HiArrowNarrowRight color="white" size={18} className="ml-2.5" />
               </Button>
            </div>
         </div>
         {/* Paging */}
         <div className="hidden lg:flex items-center space-x-2.5 justify-end min-w-fit">
            <div className="flex">
               <button
                  type="button"
                  className="flex items-center justify-center bg-[#EDEDED] hover:bg-[#DDDDDD] rounded-l-md w-8.5 h-8.5 disabled:cursor-not-allowed"
                  disabled={disabled}
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => handlePrevPage(e)}
               >
                  <HiOutlineChevronLeft size={20} color="black" />
               </button>
               <input
                  name="page"
                  value={parseInt(String(value) || "0")?.toString()}
                  maxLength={3}
                  max={total}
                  type="number"
                  inputMode="numeric"
                  className="bg-white text-xs border border-[#EDEDED] focus:border-[#E8E8E8] text-center w-11.5 h-8.5"
                  disabled={disabled}
                  onKeyDown={handlePageNumberKeydown}
                  onPaste={handlePageNumberPaste}
                  onChange={handlePageNumberChanged}
               />
               <button
                  type="button"
                  className="flex items-center justify-center bg-[#EDEDED] hover:bg-[#DDDDDD] rounded-r-md w-8.5 h-8.5 disabled:cursor-not-allowed"
                  disabled={disabled}
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleNextPage(e)}
               >
                  <HiOutlineChevronRight size={20} color="black" />
               </button>
            </div>
            <span className="text-xs">{`of ${total}`}</span>
         </div>
      </div>
   )
}

export default TablePagination